import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { lazyWithPreload, preloadAllPages } from './utils/lazyWithPreload';
import NavigationProgress, {
  startNavigationProgress,
} from './components/common/NavigationProgress';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  NavigateOptions,
} from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UnsavedChangesProvider, useUnsavedChanges } from './context/UnsavedChangesContext';
import { TranslationProvider } from './context/TranslationContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { SocketProvider } from './contexts/SocketContext';
import { RFQProvider } from './contexts/RFQContext';
import { API_ENDPOINTS } from './utils/config';
import { getRFQRequestIdFromPath } from './utils/rfqRouteParser';
import { canSeeAnyProduct } from './utils/productRights';
import { useTranslation } from './context/TranslationContext';
import LandingPage from './components/LandingPage';
import SsoCallbackPage from './pages/SsoCallbackPage';
// OpenRFQsDashboard removed - replaced by AdminRFQDashboard
// User RFQ Components
// User V2 Components
import type { AdminUser } from './types/admin-v2';
import {
  useApiInterceptor,
  setupGlobalFetchInterceptor,
} from './hooks/useApiInterceptor';
import { getDynamicSubItems } from './utils/getDynamicSubItems';
import GlobalLoader from './components/common/GlobalLoader';
import Toast from './components/Toast';
import './App.css';

// Route-level code splitting: each dashboard loads when its route is first
// visited. The shell (Header, LandingPage, SSO callback, common/) stays static.
const PrivacyPolicy = lazyWithPreload(() => import('./pages/PrivacyPolicy'));
const GeneralTerms = lazyWithPreload(() => import('./pages/GeneralTerms'));
const AdminDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/AdminDashboard'), 'admin');
const TaskManagerPage = lazyWithPreload(() => import('./components/admin-v2/pages/TaskManagerPage'), 'admin');
const AgentHealthPage = lazyWithPreload(() => import('./components/admin-v2/pages/AgentHealthPage'), 'admin');
const CustomerBugsPage = lazyWithPreload(() => import('./components/admin-v2/pages/CustomerBugsPage'), 'admin');
const RFQManualProductsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/RFQManualProductsDashboard'), 'admin');
const UserRFQManualProductsDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/UserRFQManualProductsDashboard'));
const AdminRFQDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/AdminRFQDashboard'), 'admin');
const DeletedRFQDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/DeletedRFQDashboard'), 'admin');
const SourcingOpportunitiesDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/SourcingOpportunitiesDashboard'), 'admin');
const CompanyDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyDashboard'), 'admin');
const CompanyUsersDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyUsersDashboard'));
const CompanyProductsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyProductsDashboard'), 'admin');
const CompanyMarketplaceDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyMarketplaceDashboard'));
const CompanyProductDetailsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyProductDetailsDashboard'));
const CompanySuppliersDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanySuppliersDashboard'));
const CompanyCategoriesDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanyCategoriesDashboard'));
const SupplierDetailsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/SupplierDetailsDashboard'), 'user');
const CompanySettingsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CompanySettingsDashboard'), 'admin');
const CreateRequestDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/CreateRequestDashboard'), 'user');
const ManualProductDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/ManualProductDashboard'), 'user');
const InboxDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/InboxDashboard'), 'user');
const SettingsDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/SettingsDashboard'), 'user');
const CatalogApprovalsDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/CatalogApprovalsDashboard'), 'user');
const UserSourcingOpportunitiesDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/UserSourcingOpportunitiesDashboard'), 'user');
const UserChangeSupplierDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/UserChangeSupplierDashboard'), 'user');
const UserDocumentMatchDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/UserDocumentMatchDashboard'), 'user');
const UserDocumentsDashboard = lazyWithPreload(() => import('./components/user-v2/dashboard/UserDocumentsDashboard'), 'user');
const ChangeSupplierDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/ChangeSupplierDashboard'), 'admin');
const CanonicalProductsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CanonicalProductsDashboard'), 'admin');
const CanonicalDetailsDashboard = lazyWithPreload(() => import('./components/admin-v2/dashboard/CanonicalDetailsDashboard'), 'admin');
const UserLayoutShell = lazyWithPreload(() => import('./components/user-v2/layout/UserLayoutShell'), 'user');
const AdminLayoutShell = lazyWithPreload(() => import('./components/admin-v2/layout/AdminLayoutShell'), 'admin');
const CartPage = lazyWithPreload(() => import('./components/user-v2/pages/CartPage'), 'user');
const TokenExpiredModal = lazyWithPreload(() => import('./components/TokenExpiredModal'));
const Footer = lazyWithPreload(() => import('./components/Footer'));


function AppContent() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const rawNavigate = useNavigate();
  // UYGULAMA ICI GEZINME BEKCISI (sheet 176): kaydedilmemis degisiklik
  // varken sidebar/link ile baska bir yere gecerken once sorulur. Tek yer:
  // butun admin ve kullanici ekranlari `onNavigate`'i buradan aliyor.
  const { requestLeave } = useUnsavedChanges();
  const navigate = useCallback(
    (route: string | number, options?: { replace?: boolean }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const go = () => {
        startNavigationProgress(typeof route === 'string' ? route : undefined);
        (rawNavigate as any)(route, options);
      };
      if (typeof route !== 'string') {
        go();
        return;
      }
      requestLeave(go);
    },
    [rawNavigate, requestLeave]
  ) as typeof rawNavigate;
  const [userType, setUserType] = useState<
    'user' | 'admin' | 'useradmin' | null
  >(null);
  const [showTokenExpiredModal, setShowTokenExpiredModal] = useState(false);

  // Giriş yapılınca sayfa dosyaları boşta önceden iner: sonraki gezinmeler
  // dosya beklemez (owner 2026-10-04, "sidebar'dan gidemiyor").
  React.useEffect(() => {
    // Yalnız bu rolün açabileceği sayfalar (bkz. lazyWithPreload kapsam notu).
    if (isAuthenticated && userType)
      preloadAllPages(userType === 'admin' ? 'admin' : 'user');
  }, [isAuthenticated, userType]);

  // Update userType when user changes
  React.useEffect(() => {
    if (user) {
      const isAdmin = user.is_admin === true || user.role === 'admin';
      console.log('👤 UserType effect:', {
        email: user.email,
        is_admin: user.is_admin,
        role: user.role,
        computed_isAdmin: isAdmin,
      });

      let newUserType: 'user' | 'admin';
      if (isAdmin) {
        newUserType = 'admin';
      } else {
        newUserType = 'user';
      }

      setUserType(newUserType);
      localStorage.setItem('user_type', newUserType);
    } else {
      setUserType(null);
      localStorage.removeItem('user_type');
    }
  }, [user]);

  // Setup token expired callback
  const handleTokenExpired = () => {
    console.log('🔒 Token expired callback triggered');
    // Otomatik logout yap
    logout();
    setUserType(null);
    setShowTokenExpiredModal(true);
  };

  // Setup token expired callback - temporarily disabled
  // useApiInterceptor(handleTokenExpired);

  // useEffect(() => {
  //   // Setup global fetch interceptor
  //   setupGlobalFetchInterceptor();
  // }, []);

  const handleLogin = (type: 'user' | 'admin') => {
    setUserType(type);
    // Navigate to appropriate page after login
    if (type === 'admin') {
      navigate('/dashboard/customers');
    } else {
      navigate('/dashboard/user/inbox/open');
    }
  };

  const handleLandingPageLogin = () => {
    navigate('/signin');
  };

  const handleLogout = () => {
    // AuthContext.logout now handles the portal redirect itself. Set local
    // UI state for the brief moment before the page unloads so any
    // mid-flight render shows a clean unauthenticated shell.
    setUserType(null);
    setShowTokenExpiredModal(false);
    logout();
  };

  const handleTokenExpiredLogin = () => {
    setShowTokenExpiredModal(false);
    navigate('/signin');
  };

  if (isLoading) {
    return <GlobalLoader />;
  }

  // Handle routing based on URL and authentication status
  // If not authenticated, redirect to Portal for login
  if (!isAuthenticated) {
    // Check if there's a token in URL - if so, wait for AuthContext to process it
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokenInUrl = urlParams.has('token');

    // Check if SSO login is in progress (prevents race condition)
    const ssoInProgress =
      sessionStorage.getItem('sso_login_in_progress') === 'true';

    if (hasTokenInUrl || ssoInProgress) {
      // Token is in URL or SSO is being processed, AuthContext will handle it - show loading
      return <GlobalLoader label="Signing you in" />;
    }

    // Allow privacy policy, terms, and SSO callback pages without auth
    if (
      location.pathname === '/privacy-policy' ||
      location.pathname === '/general-terms' ||
      location.pathname === '/auth/sso-callback'
    ) {
      // These routes will be handled by the Routes below
    } else {
      // Redirect to Portal for authentication
      const portalUrl =
        process.env.REACT_APP_PORTAL_URL || 'http://localhost:3001';
      const currentUrl = window.location.href;
      window.location.href = `${portalUrl}/login?redirect=${encodeURIComponent(currentUrl)}`;
      return <GlobalLoader label="Redirecting" />;
    }
  }

  // If authenticated but userType not yet determined, show loading
  if (isAuthenticated && !userType) {
    return <GlobalLoader />;
  }

  // Authenticated with known userType — handle redirects.
  // Use the declarative <Navigate> instead of calling navigate() during
  // render: an imperative navigate() in the component body triggers
  // "Cannot update a component (BrowserRouter) while rendering AppContent",
  // which forces extra render cycles and makes child pages (e.g. the
  // communication draft To/CC fields) flicker on navigation.
  if (isAuthenticated && userType) {
    // Admin on user routes → redirect to admin dashboard
    if (
      userType === 'admin' &&
      location.pathname.startsWith('/dashboard/user/')
    ) {
      return <Navigate to="/dashboard/customers" replace />;
    }
    // Approval and control notifications deep-linked to
    // /dashboard/user/approve-rfq/<id>, a path nothing in AppContent renders:
    // the page written for it was never wired in, so the link opened a blank
    // screen. The approve and control actions live in the RFQ detail header,
    // so send the reader there. Kept as a redirect rather than dropped because
    // notifications already stored in the DB still carry the old path.
    const approveRfqMatch = location.pathname.match(
      /^\/dashboard\/user\/approve-rfq\/([^/]+)\/?$/
    );
    if (approveRfqMatch) {
      return (
        <Navigate to={`/dashboard/user/rfq/${approveRfqMatch[1]}`} replace />
      );
    }
    // Redirect /signin, /, /dashboard to appropriate page
    if (
      location.pathname === '/signin' ||
      location.pathname === '/' ||
      location.pathname === '/dashboard'
    ) {
      return (
        <Navigate
          to={
            userType === 'admin'
              ? '/dashboard/customers'
              : '/dashboard/user/inbox/open'
          }
          replace
        />
      );
    }
  }

  return (
    <div className="App">
      {/* Every signed-in screen lives under /dashboard; anything else goes home. */}
      {location.pathname === '/dashboard' ||
      location.pathname.startsWith('/dashboard/') ? (
        <>
          {userType === 'admin' ? (
            <AdminLayoutShell>
              {/* Admin-specific dynamic routes - check BEFORE AdminDashboard */}
              {location.pathname.startsWith('/dashboard/companies/') &&
              location.pathname !== '/dashboard/companies' &&
              location.pathname !== '/dashboard/customers' ? (
                // Route to specific company sub-dashboards
                location.pathname.endsWith('/users') ? (
                  <CompanyUsersDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/products') ? (
                  <CompanyMarketplaceDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                    companyId={location.pathname.split('/')[3] || ''}
                  />
                ) : location.pathname.endsWith('/product-details') ? (
                  <CompanyProductDetailsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/suppliers') ? (
                  <CompanySuppliersDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/categories') ? (
                  // Kategori Listesi (owner 2026-10-12).
                  <CompanyCategoriesDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : location.pathname.endsWith('/user-rights') ? (
                  // User Right Definition now lives inside Settings. Redirect
                  // any old /user-rights link to the Settings page.
                  <Navigate
                    to={location.pathname.replace(
                      /\/user-rights$/,
                      '/settings'
                    )}
                    replace
                  />
                ) : location.pathname.endsWith('/settings') ? (
                  <CompanySettingsDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                ) : (
                  <CompanyDashboard
                    user={{
                      id: user?._id || '',
                      name: user?.name || 'Admin',
                      email: user?.email || '',
                      role: 'admin',
                      permissions: ['company_management'],
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )
              ) : location.pathname.startsWith('/dashboard/rfqs/open') &&
                !location.pathname.match(/\/dashboard\/rfqs\/open\/[^/]+/) ? (
                // Open RFQs Dashboard (new inbox-style with chat)
                <AdminRFQDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string, options?: NavigateOptions) =>
                    navigate(route, options)
                  }
                  onLogout={handleLogout}
                  phaseFilter="open"
                />
              ) : location.pathname.startsWith('/dashboard/rfqs/archived') ? (
                // Archived RFQs Dashboard (new inbox-style with chat)
                <AdminRFQDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string, options?: NavigateOptions) =>
                    navigate(route, options)
                  }
                  onLogout={handleLogout}
                  phaseFilter="archived"
                />
              ) : location.pathname.startsWith('/dashboard/rfqs/deleted') ? (
                // Deleted RFQs Dashboard (admin restore view)
                <DeletedRFQDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname.startsWith(
                  '/dashboard/sourcing-opportunities'
                ) ? (
                // Sourcing Opportunities analytics (admin + useradmin)
                <SourcingOpportunitiesDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string, opts?: { replace?: boolean }) =>
                    navigate(route, opts)
                  }
                  onLogout={handleLogout}
                />
              ) : location.pathname.startsWith('/dashboard/change-supplier') ? (
                // Change Supplier global inbox (Gordian admin)
                <ChangeSupplierDashboard
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname.startsWith(
                  '/dashboard/canonical-products/details'
                ) ? (
                // Canonical detail: attached market products (Faz 1)
                <CanonicalDetailsDashboard
                  user={user}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname.startsWith(
                  '/dashboard/canonical-products'
                ) ? (
                // Canonical Products (Gordian admin, catalog redesign Faz 1)
                <CanonicalProductsDashboard
                  user={user}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route: string) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname.startsWith('/dashboard/rfqs/open/') &&
                location.pathname !== '/dashboard/rfqs/open' ? (
                // RFQ Detail sub-routes — wrapped in RFQProvider for shared state
                (() => {
                  const rfqRequestId = getRFQRequestIdFromPath(
                    location.pathname
                  );
                  const adminUser = {
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin' as const,
                    permissions: ['company_management'],
                  };
                  const adminRoute = location.pathname + location.search;
                  const adminNav = (route: string) => navigate(route);
                  // The customer's single-page RFQ screen serves the admin too
                  // (2026-09-03). Its old Search Suppliers / Communication /
                  // Quotes Review sub-pages were removed on 2026-09-09; their
                  // routes land on the matching tab. Manual-product
                  // resolution keeps its own page.
                  const rfqBase = `/dashboard/rfqs/open/${rfqRequestId}`;
                  const commMatch = location.pathname.match(
                    /\/dashboard\/rfqs\/open\/[^/]+\/communication(?:\/([^/]+))?$/
                  );
                  const adminPageUser = {
                    ...adminUser,
                    _id: user?._id || '',
                    is_admin: true,
                    company_id: user?.company_id || '',
                    company: user?.company || '',
                  };

                  const page = location.pathname.match(
                    /\/dashboard\/rfqs\/open\/[^/]+\/manual-products(?:\/[^/]+)?$/
                  ) ? (
                    <RFQManualProductsDashboard
                      user={adminUser}
                      currentRoute={adminRoute}
                      onNavigate={adminNav}
                      onLogout={handleLogout}
                    />
                  ) : commMatch ? (
                    <Navigate
                      to={`${rfqBase}?tab=communication${
                        commMatch[1] ? `&supplier=${commMatch[1]}` : ''
                      }`}
                      replace
                    />
                  ) : location.pathname.match(
                      /\/dashboard\/rfqs\/open\/[^/]+\/quotes-review$/
                    ) ? (
                    <Navigate to={`${rfqBase}?tab=overview`} replace />
                  ) : location.pathname.match(
                      /\/dashboard\/rfqs\/open\/[^/]+\/suppliers/
                    ) ? (
                    <Navigate to={`${rfqBase}?tab=communication`} replace />
                  ) : location.pathname.match(
                      /\/dashboard\/rfqs\/open\/[^/]+\/products$/
                    ) ? (
                    // Product Overview page was removed — products are edited
                    // inline on the RFQ Review page. Redirect stale deep links.
                    <Navigate
                      to={
                        location.pathname.replace(/\/products$/, '') +
                        location.search
                      }
                      replace
                    />
                  ) : (
                    <UserRFQManualProductsDashboard
                      user={adminPageUser}
                      currentRoute={adminRoute}
                      onNavigate={adminNav}
                      onLogout={handleLogout}
                      layoutType="admin"
                    />
                  );

                  // Wrap in RFQProvider only when we have a valid requestId
                  return rfqRequestId ? (
                    <RFQProvider requestId={rfqRequestId}>{page}</RFQProvider>
                  ) : (
                    page
                  );
                })()
              ) : location.pathname === '/dashboard/agent-health' ? (
                <AgentHealthPage
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname === '/dashboard/customer-bugs' ? (
                <CustomerBugsPage
                  user={{
                    id: user?._id || '',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    role: 'admin',
                    permissions: ['company_management'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : location.pathname === '/dashboard/task-manager' ? (
                <TaskManagerPage
                  user={{
                    _id: user?._id || '',
                    role: 'admin',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route) => navigate(route)}
                  onLogout={handleLogout}
                />
              ) : (
                <AdminDashboard
                  user={{
                    id: user?._id || '',
                    role: 'admin',
                    name: user?.name || 'Admin',
                    email: user?.email || '',
                    permissions: ['all'],
                  }}
                  currentRoute={location.pathname + location.search}
                  onNavigate={(route) => navigate(route)}
                  onLogout={handleLogout}
                />
              )}
            </AdminLayoutShell>
          ) : userType === 'user' ? (
            // User V2 Routes (Permission-based access)
            <>
              {/*
                Inbox + Settings share a single persistent UserLayoutShell
                so sidebar state (badges/fetches) survives navigation
                between them. Other dashboards still wrap themselves in
                their own UserLayout; they'll be migrated gradually.
              */}
              {((user?.permissions?.open_rfq_request &&
                (location.pathname === '/dashboard/user/inbox/open' ||
                  location.pathname === '/dashboard/user/inbox/archived' ||
                  location.pathname === '/dashboard/user/inbox' ||
                  location.pathname ===
                    '/dashboard/user/create-request/marketplace' ||
                  location.pathname === '/dashboard/user/cart')) ||
                location.pathname === '/dashboard/user/settings' ||
                location.pathname.startsWith(
                  '/dashboard/user/sourcing-opportunities'
                ) ||
                location.pathname === '/dashboard/user/change-supplier' ||
                location.pathname === '/dashboard/user/document-match' ||
                location.pathname === '/dashboard/user/documents' ||
                location.pathname === '/dashboard/user/catalog-approvals') && (
                <UserLayoutShell>
                  {user?.permissions?.open_rfq_request &&
                    location.pathname === '/dashboard/user/inbox/open' && (
                      <InboxDashboard
                        key="inbox-open"
                        user={{
                          id: user?._id || '',
                          _id: user?._id || '',
                          role: 'user',
                          name: user?.name || 'User',
                          email: user?.email || '',
                          company: user?.company || '',
                          company_id:
                            user?.company_id ||
                            (typeof user?.company === 'string'
                              ? user?.company
                              : user?.company?._id) ||
                            '',
                          permissions: user?.permissions || {},
                        }}
                        currentRoute={location.pathname + location.search}
                        onNavigate={(route: string) => navigate(route)}
                        onLogout={handleLogout}
                        phaseFilter="open"
                      />
                    )}
                  {user?.permissions?.open_rfq_request &&
                    location.pathname === '/dashboard/user/inbox/archived' && (
                      <InboxDashboard
                        key="inbox-archived"
                        user={{
                          id: user?._id || '',
                          _id: user?._id || '',
                          role: 'user',
                          name: user?.name || 'User',
                          email: user?.email || '',
                          company: user?.company || '',
                          company_id:
                            user?.company_id ||
                            (typeof user?.company === 'string'
                              ? user?.company
                              : user?.company?._id) ||
                            '',
                          permissions: user?.permissions || {},
                        }}
                        currentRoute={location.pathname + location.search}
                        onNavigate={(route: string) => navigate(route)}
                        onLogout={handleLogout}
                        phaseFilter="archived"
                      />
                    )}
                  {location.pathname === '/dashboard/user/inbox' && (
                    <InboxDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        role: 'user',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                  {location.pathname === '/dashboard/user/settings' && (
                    <SettingsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        role: user?.role || 'user',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                        is_admin: user?.is_admin || false,
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                  {user?.permissions?.open_rfq_request &&
                    location.pathname ===
                      '/dashboard/user/create-request/marketplace' && (
                      <CreateRequestDashboard
                        user={{
                          id: user?._id || '',
                          _id: user?._id || '',
                          role: 'user',
                          name: user?.name || 'User',
                          email: user?.email || '',
                          company: user?.company || '',
                          company_id:
                            user?.company_id ||
                            (typeof user?.company === 'string'
                              ? user?.company
                              : user?.company?._id) ||
                            '',
                          permissions: user?.permissions || {},
                        }}
                        currentRoute={location.pathname + location.search}
                        onNavigate={(route: string) => navigate(route)}
                        onLogout={handleLogout}
                      />
                    )}
                  {user?.permissions?.open_rfq_request &&
                    location.pathname === '/dashboard/user/cart' && (
                      <CartPage />
                    )}
                  {location.pathname ===
                    '/dashboard/user/catalog-approvals' && (
                    <CatalogApprovalsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        company_name:
                          (typeof user?.company === 'string'
                            ? ''
                            : user?.company?.name) || '',
                        permissions: (user?.permissions ||
                          {}) as AdminUser['permissions'],
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                  {location.pathname.startsWith(
                    '/dashboard/user/sourcing-opportunities'
                  ) && (
                    <UserSourcingOpportunitiesDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: (user?.permissions ||
                          {}) as AdminUser['permissions'],
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string, opts?: { replace?: boolean }) =>
                        navigate(route, opts)
                      }
                      onLogout={handleLogout}
                    />
                  )}
                  {location.pathname === '/dashboard/user/change-supplier' && (
                    <UserChangeSupplierDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: (user?.permissions ||
                          {}) as AdminUser['permissions'],
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                  {location.pathname === '/dashboard/user/document-match' && (
                    <UserDocumentMatchDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: (user?.permissions ||
                          {}) as AdminUser['permissions'],
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                  {location.pathname === '/dashboard/user/documents' && (
                    <UserDocumentsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: (user?.permissions ||
                          {}) as AdminUser['permissions'],
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  )}
                </UserLayoutShell>
              )}
              {/* Legacy routes - redirect to new inbox routes */}
              {user?.permissions?.open_rfq_request &&
                location.pathname === '/dashboard/user/open-rfq' && (
                  <Navigate to="/dashboard/user/inbox/open" replace />
                )}
              {user?.permissions?.open_rfq_request &&
                location.pathname === '/dashboard/user/closed-rfq' && (
                  <Navigate to="/dashboard/user/inbox/archived" replace />
                )}
              {/* /dashboard/user/create-request/marketplace handled inside
                  the UserLayoutShell block above. */}
              {user?.permissions?.open_rfq_request &&
                location.pathname ===
                  '/dashboard/user/create-request/manual' && (
                  <ManualProductDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      role: 'user',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      company: user?.company || '',
                      company_id:
                        user?.company_id ||
                        (typeof user?.company === 'string'
                          ? user?.company
                          : user?.company?._id) ||
                        '',
                      permissions: user?.permissions || {},
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )}
              {/* /dashboard/user/inbox, /dashboard/user/settings, and
                  /dashboard/user/cart are handled by the UserLayoutShell
                  block above. */}

              {/* Basic plan user RFQ Detail - UserLayout with 2nd sidebar for /dashboard/user/rfq/{id} */}
              {location.pathname.startsWith('/dashboard/user/rfq/') &&
                location.pathname !== '/dashboard/user/rfq' &&
                !location.pathname.includes('/products') &&
                !location.pathname.includes('/manual-products') &&
                !location.pathname.includes('/quotes-review') &&
                !location.pathname.includes('/suppliers') &&
                !location.pathname.includes('/communication') && (
                  <UserRFQManualProductsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: (user?.role || 'user') as any,
                      is_admin: user?.is_admin,
                      permissions: user?.permissions || {},
                      company_id:
                        user?.company_id ||
                        (typeof user?.company === 'string'
                          ? user?.company
                          : user?.company?._id) ||
                        '',
                      company: user?.company || '',
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )}
              {/* User RFQ sub-routes. The standalone Quotes Review /
                  Communication / Supplier Search pages were removed
                  (2026-09-09): customers never operated them (the backend
                  answers 403 and useRFQAccessGuard bounced them straight
                  back), so a deep link now lands on the matching tab of the
                  single RFQ Review page. Manual-product deep links keep
                  opening the review page itself. */}
              {location.pathname.startsWith('/dashboard/user/rfq/') &&
                location.pathname !== '/dashboard/user/rfq' &&
                (location.pathname.includes('/manual-products') ||
                  location.pathname.includes('/quotes-review') ||
                  location.pathname.includes('/suppliers') ||
                  location.pathname.includes('/communication')) &&
                (() => {
                  const rfqRequestId = getRFQRequestIdFromPath(
                    location.pathname
                  );
                  const reviewBase = `/dashboard/user/rfq/${rfqRequestId}`;
                  const tabParams = new URLSearchParams(location.search);
                  const commMatch = location.pathname.match(
                    /\/dashboard\/user\/rfq\/[^/]+\/communication(?:\/([^/]+))?$/
                  );
                  if (
                    location.pathname.match(
                      /\/dashboard\/user\/rfq\/[^/]+\/quotes-review$/
                    )
                  ) {
                    tabParams.set('tab', 'overview');
                    return (
                      <Navigate
                        to={`${reviewBase}?${tabParams.toString()}`}
                        replace
                      />
                    );
                  }
                  if (commMatch) {
                    tabParams.set('tab', 'communication');
                    if (commMatch[1]) tabParams.set('supplier', commMatch[1]);
                    return (
                      <Navigate
                        to={`${reviewBase}?${tabParams.toString()}`}
                        replace
                      />
                    );
                  }
                  if (
                    location.pathname.match(
                      /\/dashboard\/user\/rfq\/[^/]+\/suppliers/
                    )
                  ) {
                    tabParams.set('tab', 'communication');
                    return (
                      <Navigate
                        to={`${reviewBase}?${tabParams.toString()}`}
                        replace
                      />
                    );
                  }

                  const page = (
                    <UserRFQManualProductsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: (user?.role || 'user') as any,
                        is_admin: user?.is_admin,
                        permissions: user?.permissions || {},
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        company: user?.company || '',
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  );

                  return rfqRequestId ? (
                    <RFQProvider requestId={rfqRequestId}>{page}</RFQProvider>
                  ) : (
                    page
                  );
                })()}
              {/* Regular user RFQ sub-routes under /dashboard/rfqs/open/ */}
              {location.pathname.startsWith('/dashboard/rfqs/open/') &&
                location.pathname !== '/dashboard/rfqs/open' &&
                !location.pathname.includes('/products') &&
                (location.pathname.includes('/communication') ||
                  location.pathname.includes('/suppliers') ||
                  location.pathname.includes('/quotes-review')) &&
                (() => {
                  const rfqMatch = location.pathname.match(
                    /\/dashboard\/rfqs\/open\/([^/]+)/
                  );
                  const rfqId = rfqMatch?.[1] || '';
                  const userProps = {
                    id: user?._id || '',
                    _id: user?._id || '',
                    name: user?.name || 'User',
                    email: user?.email || '',
                    role: (user?.role || 'user') as any,
                    is_admin: user?.is_admin,
                    permissions: user?.permissions || {},
                    company_id:
                      user?.company_id ||
                      (typeof user?.company === 'string'
                        ? user?.company
                        : user?.company?._id) ||
                      '',
                    company: user?.company || '',
                  };
                  const userRoute = `/dashboard/user/rfq/${rfqId}${location.pathname.replace(/\/dashboard\/rfqs\/open\/[^/]+/, '')}`;
                  // Redirect to user route so component picks up correct base
                  navigate(userRoute + location.search, { replace: true });
                  return null;
                })()}
              {/* Regular user RFQ Detail - for all /dashboard/rfqs/open/{id} routes (both with and without approve_ability) */}
              {location.pathname.startsWith('/dashboard/rfqs/open/') &&
                location.pathname !== '/dashboard/rfqs/open' &&
                !location.pathname.includes('/products') &&
                !location.pathname.includes('/manual-products') &&
                !location.pathname.includes('/quotes-review') &&
                !location.pathname.includes('/suppliers') &&
                !location.pathname.includes('/communication') &&
                (
                  <UserRFQManualProductsDashboard
                    user={{
                      id: user?._id || '',
                      _id: user?._id || '',
                      name: user?.name || 'User',
                      email: user?.email || '',
                      role: (user?.role || 'user') as any,
                      is_admin: user?.is_admin,
                      permissions: user?.permissions || {},
                      company_id:
                        user?.company_id ||
                        (typeof user?.company === 'string'
                          ? user?.company
                          : user?.company?._id) ||
                        '',
                      company: user?.company || '',
                    }}
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                )}
              {/* User company management routes - permission-based access.
                  Wrapped in UserLayoutShell so the sidebar survives navigation
                  between these pages (and to/from inbox + settings).
                  DataDashboard inside these dashboards auto-detects the shell
                  via useIsInsidePageChromeShell and skips its own UserLayout. */}
              {location.pathname.startsWith('/dashboard/companies/') &&
              location.pathname !== '/dashboard/companies' &&
              location.pathname !== '/dashboard/customers' ? (
                <UserLayoutShell>
                  {location.pathname.endsWith('/products') &&
                  user?.permissions?.edit_product &&
                  canSeeAnyProduct(user?.permissions) ? (
                    <CompanyMarketplaceDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                      companyId={location.pathname.split('/')[3] || ''}
                    />
                  ) : location.pathname.endsWith('/product-details') &&
                    user?.permissions?.edit_product &&
                    canSeeAnyProduct(user?.permissions) ? (
                    <CompanyProductDetailsDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.endsWith('/suppliers') &&
                    user?.permissions?.edit_supplier ? (
                    <CompanySuppliersDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.endsWith('/categories') &&
                    user?.permissions?.edit_supplier ? (
                    // Kategori Listesi (owner 2026-10-12): Tedarikçi
                    // Listesi'yle aynı izin.
                    <CompanyCategoriesDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.endsWith('/users') &&
                    user?.permissions?.user_rights ? (
                    <CompanyUsersDashboard
                      user={{
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      }}
                      currentRoute={location.pathname + location.search}
                      onNavigate={(route: string) => navigate(route)}
                      onLogout={handleLogout}
                    />
                  ) : location.pathname.endsWith('/user-rights') &&
                    user?.permissions?.user_rights ? (
                    // User Right Definition moved into the Settings page (User
                    // Rights tab). Redirect old links there.
                    <Navigate
                      to="/dashboard/user/settings?tab=user-rights"
                      replace
                    />
                  ) : null}
                </UserLayoutShell>
              ) : null}
              {/* Supplier detail page for a company-admin: reached by double-clicking
                  a row on the company suppliers page. Renders the same detail view
                  as the admin flow (supplier's product list), wrapped in the user
                  shell so the sidebar survives. */}
              {location.pathname === '/dashboard/suppliers/details' &&
              user?.permissions?.edit_supplier ? (
                <UserLayoutShell>
                  <SupplierDetailsDashboard
                    user={
                      {
                        id: user?._id || '',
                        _id: user?._id || '',
                        name: user?.name || 'User',
                        email: user?.email || '',
                        role: 'useradmin',
                        company: user?.company || '',
                        company_id:
                          user?.company_id ||
                          (typeof user?.company === 'string'
                            ? user?.company
                            : user?.company?._id) ||
                          '',
                        permissions: user?.permissions || {},
                      } as any
                    }
                    currentRoute={location.pathname + location.search}
                    onNavigate={(route: string) => navigate(route)}
                    onLogout={handleLogout}
                  />
                </UserLayoutShell>
              ) : null}
              {/* Fallback: no permission for current route — show access denied instead of white screen */}
              {!user?.permissions?.open_rfq_request &&
                (location.pathname === '/dashboard/user/inbox/open' ||
                  location.pathname === '/dashboard/user/inbox/archived') && (
                  <div style={{ padding: '60px 40px', textAlign: 'center' }}>
                    <h2 style={{ marginBottom: '12px' }}>Access Denied</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                      You don't have permission to view this page. Please
                      contact your administrator.
                    </p>
                    <button
                      onClick={handleLogout}
                      style={{ padding: '8px 20px', cursor: 'pointer' }}
                    >
                      Logout
                    </button>
                  </div>
                )}
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '60vh',
              }}
            >
              <p>Loading...</p>
            </div>
          )}
        </>
      ) : (
        <Navigate
          to={
            userType === 'admin'
              ? '/dashboard/customers'
              : '/dashboard/user/inbox/open'
          }
          replace
        />
      )}

      {/* Token Expired Modal */}
      <TokenExpiredModal
        isOpen={showTokenExpiredModal}
        onClose={() => setShowTokenExpiredModal(false)}
        onLogin={handleTokenExpiredLogin}
      />
    </div>
  );
}

function App() {
  return (
    <Router>
      <TranslationProvider>
        <AuthProvider>
          <SocketProvider>
            <CurrencyProvider>
              {/* Kaydedilmemis degisiklik bekcisi: sekme kapatma, tarayici
                  geri tusu ve uygulama ici gezinme (sheet 176). */}
              <UnsavedChangesProvider>
              <NavigationProgress />
              <Suspense fallback={<div className="route-loading" aria-busy="true" />}>
              <Routes>
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/general-terms" element={<GeneralTerms />} />
                <Route
                  path="/auth/sso-callback"
                  element={<SsoCallbackPage />}
                />
                <Route path="/signin" element={<AppContent />} />
                <Route path="/dashboard" element={<AppContent />} />
                <Route
                  path="/dashboard/agent-health"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/task-manager"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/customer-bugs"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/canonical-products"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/canonical-products/details"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/all-products"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/categories-v2"
                  element={<AppContent />}
                />
                <Route path="/dashboard/bindings" element={<AppContent />} />
                <Route
                  path="/dashboard/catalog-control"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/inbox/open"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/inbox/archived"
                  element={<AppContent />}
                />
                {/* Legacy routes - redirect to new inbox routes */}
                <Route
                  path="/dashboard/user/open-rfq"
                  element={<Navigate to="/dashboard/user/inbox/open" replace />}
                />
                <Route
                  path="/dashboard/user/closed-rfq"
                  element={
                    <Navigate to="/dashboard/user/inbox/archived" replace />
                  }
                />
                {/* Renamed Finalized -> Archived: keep old links working */}
                <Route
                  path="/dashboard/user/inbox/finalized"
                  element={
                    <Navigate to="/dashboard/user/inbox/archived" replace />
                  }
                />
                <Route
                  path="/dashboard/rfqs/finalized"
                  element={<Navigate to="/dashboard/rfqs/archived" replace />}
                />
                <Route
                  path="/dashboard/user/create-request/marketplace"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/create-request/manual"
                  element={<AppContent />}
                />
                <Route path="/dashboard/user/inbox" element={<AppContent />} />
                <Route
                  path="/dashboard/user/settings"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/catalog-approvals"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/document-match"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/documents"
                  element={<AppContent />}
                />
                {/* Basic plan user routes - UserLayout with 2nd sidebar */}
                <Route
                  path="/dashboard/user/rfq/:requestId/communication/:supplierId?"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/rfq/:requestId/suppliers/:productId"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/rfq/:requestId/*"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/rfq/:requestId"
                  element={<AppContent />}
                />
                <Route path="/dashboard/rfqs/open" element={<AppContent />} />
                <Route
                  path="/dashboard/rfqs/open/:requestId"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/rfqs/open/:requestId/communication/:supplierId?"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/user/approve-rfq/:requestId"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/rfqs/open/:requestId/suppliers/:productId"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/users"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/products"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/product-details"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/suppliers"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/categories"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/user-rights"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/companies/:companyId/settings"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/mail-dashboard"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/mail-dashboard/unmatched"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/mail-dashboard/messages"
                  element={<AppContent />}
                />
                <Route
                  path="/dashboard/suppliers/details"
                  element={<AppContent />}
                />
                <Route path="/" element={<AppContent />} />
                <Route path="*" element={<AppContent />} />
              </Routes>
              </Suspense>
              <Toast />
              </UnsavedChangesProvider>
            </CurrencyProvider>
          </SocketProvider>
        </AuthProvider>
      </TranslationProvider>
    </Router>
  );
}

export default App;
